import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

export type ProjectCreateClientType = 'particulier' | 'professionnel'
export type ProjectCreateLanguageId = string
export type ProjectCreateProjectTypeId = string

export type ProjectCreateCivilityId = string

export type ProjectCreateClientForm = {
  clientType: ProjectCreateClientType
  designation: string
  name: string
  civilityId: ProjectCreateCivilityId
  firstName: string
  lastName: string
  phone: string
  email: string
  documentLanguageId: ProjectCreateLanguageId
}

export type ProjectCreateProjectForm = {
  projectTypeId: ProjectCreateProjectTypeId | null
  projectName: string
  projectNameTouched: boolean
  isDirectSale: boolean
  vatPercent: number | null
}

export type ProjectCreateDraft = {
  selectedExistingClientId: string | null
  selectedExistingClientDisplayName: string | null
  selectedJobSiteId: string | null
  selectedJobSiteLabel: string | null
  isNewJobSite: boolean
  isNewClient: boolean
  client: ProjectCreateClientForm
  project: ProjectCreateProjectForm
}

interface ProjectCreateWizardContextValue {
  draft: ProjectCreateDraft
  setSelectedExistingClientId: (id: string | null) => void
  setSelectedExistingClientDisplayName: (name: string | null) => void
  setSelectedJobSiteId: (id: string | null) => void
  setSelectedJobSiteLabel: (label: string | null) => void
  setIsNewJobSite: (v: boolean) => void
  setIsNewClient: (v: boolean) => void
  setClientField: <K extends keyof ProjectCreateClientForm>(key: K, value: ProjectCreateClientForm[K]) => void
  setProjectField: <K extends keyof ProjectCreateProjectForm>(key: K, value: ProjectCreateProjectForm[K]) => void
}

const ProjectCreateWizardContext = createContext<ProjectCreateWizardContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useProjectCreateWizard() {
  const ctx = useContext(ProjectCreateWizardContext)
  if (!ctx) {
    throw new Error('useProjectCreateWizard must be used within ProjectCreateWizardProvider')
  }
  return ctx
}

function createDefaultDraft(): ProjectCreateDraft {
  return {
    selectedExistingClientId: null,
    selectedExistingClientDisplayName: null,
    selectedJobSiteId: null,
    selectedJobSiteLabel: null,
    isNewJobSite: false,
    isNewClient: false,
    client: {
      clientType: 'particulier',
      designation: '',
      name: '',
      civilityId: 'mr',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      // Real default is set once the catalog API resolves (org-primary language).
      // We seed an empty string here so the wizard knows it hasn't been initialized yet.
      documentLanguageId: '',
    },
    project: {
      projectTypeId: null,
      projectName: '',
      projectNameTouched: false,
      isDirectSale: false,
      vatPercent: null,
    },
  }
}

export function ProjectCreateWizardProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<ProjectCreateDraft>(() => createDefaultDraft())

  const value = useMemo<ProjectCreateWizardContextValue>(() => {
    return {
      draft,
      setSelectedExistingClientId: (id) => {
        setDraft((prev) => ({
          ...prev,
          selectedExistingClientId: id,
        }))
      },
      setSelectedExistingClientDisplayName: (name) => {
        setDraft((prev) => ({
          ...prev,
          selectedExistingClientDisplayName: name,
        }))
      },
      setSelectedJobSiteId: (id) => {
        setDraft((prev) => ({
          ...prev,
          selectedJobSiteId: id,
        }))
      },
      setSelectedJobSiteLabel: (label) => {
        setDraft((prev) => ({
          ...prev,
          selectedJobSiteLabel: label,
        }))
      },
      setIsNewJobSite: (v) => {
        setDraft((prev) => ({
          ...prev,
          isNewJobSite: v,
        }))
      },
      setIsNewClient: (v) => {
        setDraft((prev) => ({
          ...prev,
          isNewClient: v,
        }))
      },
      setClientField: (key, value) => {
        setDraft((prev) => ({
          ...prev,
          client: {
            ...prev.client,
            [key]: value,
          },
        }))
      },
      setProjectField: (key, value) => {
        setDraft((prev) => ({
          ...prev,
          project: {
            ...prev.project,
            [key]: value,
          },
        }))
      },
    }
  }, [draft])

  return <ProjectCreateWizardContext.Provider value={value}>{children}</ProjectCreateWizardContext.Provider>
}
